import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Clock, StopCircle, ShoppingCart, ClipboardEdit, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { activityFormRequired, savePendingClose, clearPendingClose } from "@/lib/callForms";
import { mutate as offlineMutate } from "@/offline/apiClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

export default function VisitInProgress() {
  const { visit_id } = useParams();
  const nav = useNavigate();
  const [visit, setVisit] = useState(null);
  const [endOpen, setEndOpen] = useState(false);
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    const { data } = await api.get(`/visits/${visit_id}`);
    setVisit(data);
  }, [visit_id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const running = useMemo(() => {
    if (!visit?.check_in_ts) return 0;
    const start = new Date(visit.check_in_ts).getTime();
    return Math.max(0, Math.floor((Date.now() - start) / 1000));
  }, [visit, tick]);

  if (!visit) return <div className="p-4 text-sm text-neutral-500">Loading…</div>;

  const inProgress = visit.status === "in_progress";
  const retailer = visit.retailer || {};

  return (
    <div className="p-4 pb-24 space-y-4" data-testid="visit-inprogress-screen">
      <button onClick={() => nav("/", { replace: true })}
              className="text-sm text-neutral-500 flex items-center gap-1"
              data-testid="visit-back-home">
        <ArrowLeft className="h-4 w-4" /> Home
      </button>

      <div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-400">
          {inProgress ? "In progress" : "Completed"}
        </div>
        <h1 className="text-xl font-extrabold text-neutral-900">{retailer.name}</h1>
        <div className="text-xs text-neutral-500 mt-0.5">
          {retailer.code} · {retailer.address}
        </div>
      </div>

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-5 text-center">
          <div className="text-[11px] uppercase tracking-widest text-neutral-400">Elapsed</div>
          <div className="text-4xl font-black tabular-nums text-[#E5197F]" data-testid="visit-timer">
            {String(Math.floor(running / 3600)).padStart(2, "0")}:
            {String(Math.floor((running % 3600) / 60)).padStart(2, "0")}:
            {String(running % 60).padStart(2, "0")}
          </div>
          <div className="text-xs text-neutral-400 mt-1">
            Started at {new Date(visit.check_in_ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </div>
        </CardContent>
      </Card>

      {inProgress && (
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" className="h-14 rounded-xl"
                  onClick={() => nav(`/orders/new?visit_id=${visit.id}&retailer_id=${visit.retailer_id}`)}
                  data-testid="visit-add-order">
            <ShoppingCart className="h-4 w-4 mr-2" /> Add Order
          </Button>
          <Button variant="outline" className="h-14 rounded-xl"
                  onClick={() => nav(`/forms?visit_id=${visit.id}&retailer_id=${visit.retailer_id}`)}
                  data-testid="visit-fill-form">
            <ClipboardEdit className="h-4 w-4 mr-2" /> Fill Form
          </Button>
        </div>
      )}

      {inProgress ? (
        <Button onClick={() => setEndOpen(true)}
                className="w-full h-14 tulsi-gradient text-white text-base font-bold rounded-2xl"
                data-testid="visit-end-btn">
          <StopCircle className="h-5 w-5 mr-2" /> End visit
        </Button>
      ) : (
        <Card className="border-neutral-200 rounded-2xl">
          <CardContent className="p-4 space-y-2">
            <div className="text-sm">
              <span className="text-neutral-500">Outcome:</span>{" "}
              <span className={`font-bold ${visit.outcome === "productive" ? "text-green-700" : "text-amber-700"}`}>
                {visit.outcome}
              </span>
            </div>
            {visit.non_productive_reason && (
              <div className="text-sm"><span className="text-neutral-500">Reason:</span> {visit.non_productive_reason}</div>
            )}
            {visit.notes && <div className="text-sm text-neutral-600">"{visit.notes}"</div>}
            <div className="text-xs text-neutral-500">
              Duration: {Math.floor((visit.duration_sec || 0) / 60)}m {(visit.duration_sec || 0) % 60}s
            </div>
          </CardContent>
        </Card>
      )}

      {endOpen && (
        <EndVisitDialog visit={visit} onClose={() => setEndOpen(false)}
                        onNeedForms={() => nav(`/field/call-forms/${visit.id}`)}
                        onDone={() => { setEndOpen(false); nav("/today-beat", { replace: true }); }} />
      )}
    </div>
  );
}

function EndVisitDialog({ visit, onClose, onDone, onNeedForms }) {
  const [outcome, setOutcome] = useState("productive");
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState("");
  // Bug 4 (hotfix drop #5A): no-order reason for productive-visit-without-order.
  const [noOrderReason, setNoOrderReason] = useState("");
  const [noOrderNote, setNoOrderNote] = useState("");
  const [remarks, setRemarks] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  // The visit has NO order booked yet when linked_order_id is empty.
  // Backend (routes_visits.py:363-369) REQUIRES no_order_reason_id when
  // outcome=productive AND no linked order. FE must surface that selector
  // or the SE hits an unrecoverable 400 (which then leaves visit as
  // in_progress → Bug 5 · beat still shows "pending"). Fixing this here
  // fixes Bug 4 and transitively resolves Bug 5.
  const hasOrder = !!visit.linked_order_id;
  const noOrderReasonRequired = outcome === "productive" && !hasOrder;

  useEffect(() => {
    api.get("/lookups", { params: { category: "visit_np_reason" } })
       .then(({ data }) => setReasons((data.items || []).filter((r) => r.code !== "auto_skipped")));
  }, []);

  const submit = async () => {
    if (outcome === "non_productive" && !reason) return toast.error("Select a reason");
    if (outcome === "non_productive" && reason === "other" && !remarks) return toast.error("Remarks required for 'other'");
    if (noOrderReasonRequired && !noOrderReason)
      return toast.error("Select a no-order reason (visit is productive but no order was booked)");
    if (noOrderReasonRequired && noOrderReason === "other" && !noOrderNote)
      return toast.error("Note required for 'other'");
    setBusy(true);
    try {
      const gps = await new Promise((res) => {
        if (!navigator.geolocation) return res(null);
        navigator.geolocation.getCurrentPosition(
          (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy_m: Math.round(p.coords.accuracy || 0) }),
          () => res(null),
          { timeout: 4000 },
        );
      });
      const body = {
        gps: gps || { lat: 0, lng: 0 },
        outcome,
        non_productive_reason: outcome === "non_productive" ? reason : null,
        non_productive_remarks: outcome === "non_productive" && reason === "other" ? remarks : null,
        // Bug 4: send the no-order reason when productive-without-order.
        no_order_reason_id: noOrderReasonRequired ? noOrderReason : null,
        no_order_note: noOrderReasonRequired ? (noOrderNote || null) : null,
        notes: notes || null,
        client_ts: new Date().toISOString(),
        idempotency_key: (crypto.randomUUID?.() || `visit-end-${Date.now()}`),
      };
      const r = await offlineMutate({ method: "POST", path: `/visits/${visit.id}/end`, body,
                                      entity_type: "visits" });
      if (!r.immediate) toast.info("Queued offline");
      else toast.success("Visit ended");
      clearPendingClose(visit.id);
      onDone();
    } catch (e) {
      const need = activityFormRequired(e);
      if (need) {
        savePendingClose(visit.id, {
          outcome, non_productive_reason: outcome === "non_productive" ? reason : null,
          non_productive_remarks: outcome === "non_productive" && reason === "other" ? remarks : null,
          no_order_reason_id: noOrderReasonRequired ? noOrderReason : null,
          no_order_note: noOrderReasonRequired ? (noOrderNote || null) : null,
          notes: notes || null,
        });
        toast.info(need.message); onNeedForms?.();
      }
      else toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="visit-end-dialog">
        <DialogHeader><DialogTitle>End visit</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-neutral-500">Outcome</Label>
            <RadioGroup value={outcome} onValueChange={setOutcome} className="grid grid-cols-2 gap-2 pt-2">
              <label className={`flex items-center gap-2 rounded-xl border p-3 cursor-pointer ${
                outcome === "productive" ? "border-green-500 bg-green-50" : "border-neutral-200"
              }`}>
                <RadioGroupItem value="productive" data-testid="outcome-productive" />
                <span className="text-sm font-semibold">Productive</span>
              </label>
              <label className={`flex items-center gap-2 rounded-xl border p-3 cursor-pointer ${
                outcome === "non_productive" ? "border-amber-500 bg-amber-50" : "border-neutral-200"
              }`}>
                <RadioGroupItem value="non_productive" data-testid="outcome-nonproductive" />
                <span className="text-sm font-semibold">Non-productive</span>
              </label>
            </RadioGroup>
          </div>

          {outcome === "non_productive" && (
            <>
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-neutral-500">Reason *</Label>
                <Select value={reason} onValueChange={setReason}>
                  <SelectTrigger data-testid="np-reason-select"><SelectValue placeholder="Choose reason" /></SelectTrigger>
                  <SelectContent>
                    {reasons.map((r) => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {reason === "other" && (
                <div>
                  <Label className="text-[11px] uppercase tracking-widest text-neutral-500">Remarks *</Label>
                  <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)}
                            data-testid="np-remarks" />
                </div>
              )}
            </>
          )}

          {/* Bug 4 (hotfix drop #5A): productive visit closed without an
              order booked → require a reason drawn from the same
              visit_np_reason lookup. Without this selector the SE cannot
              successfully close the call (backend 400s), and Today's Beat
              keeps the outlet as pending (Bug 5). */}
          {noOrderReasonRequired && (
            <div className="space-y-3 border-l-2 border-amber-400 pl-3"
                 data-testid="no-order-reason-block">
              <div className="text-[11px] text-amber-800 font-semibold uppercase tracking-widest">
                No order booked — reason required
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-neutral-500">Reason *</Label>
                <Select value={noOrderReason} onValueChange={setNoOrderReason}>
                  <SelectTrigger data-testid="no-order-reason-select">
                    <SelectValue placeholder="Choose reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {reasons.map((r) => (
                      <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {noOrderReason === "other" && (
                <div>
                  <Label className="text-[11px] uppercase tracking-widest text-neutral-500">Note *</Label>
                  <Textarea rows={2} value={noOrderNote}
                            onChange={(e) => setNoOrderNote(e.target.value)}
                            data-testid="no-order-note" />
                </div>
              )}
            </div>
          )}

          <div>
            <Label className="text-[11px] uppercase tracking-widest text-neutral-500">Notes (optional)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add any observations…" data-testid="visit-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy}
                  className="tulsi-gradient text-white" data-testid="visit-end-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            End visit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
